// Google Places autocomplete using the new `PlaceAutocompleteElement`
// (web component). Loading pattern follows the current Google guidance
// (https://developers.google.com/maps/documentation/javascript/place-autocomplete-new):
//   - script URL includes `loading=async` (required for the new element to
//     render its internal input; without it the element stays empty)
//   - the element is imported via `google.maps.importLibrary("places")`
//   - the parent's `onSelect` callback is stored in a ref so re-renders don't
//     trigger a remount (which would wipe the element the user is typing into)
import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const SCRIPT_ID = "geovinum-google-maps-async";

let loaderPromise = null;

function loadGoogleMaps() {
  if (typeof window === "undefined") return Promise.reject(new Error("SSR"));
  if (window.google?.maps?.importLibrary) return Promise.resolve();
  if (loaderPromise) return loaderPromise;
  loaderPromise = new Promise((resolve, reject) => {
    // Reuse existing script tag if present
    if (document.getElementById(SCRIPT_ID)) {
      const check = () =>
        window.google?.maps?.importLibrary
          ? resolve()
          : setTimeout(check, 60);
      check();
      return;
    }
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.async = true;
    // `loading=async` is critical — without it, PlaceAutocompleteElement is
    // constructible but its shadow DOM (the actual <input>) is never rendered.
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GMAPS_KEY}&libraries=places&language=fr&v=weekly&loading=async`;
    s.onload = () => {
      const t0 = Date.now();
      const check = () => {
        if (window.google?.maps?.importLibrary) return resolve();
        if (Date.now() - t0 > 8000) return reject(new Error("google.maps.importLibrary unavailable"));
        setTimeout(check, 80);
      };
      check();
    };
    s.onerror = () => reject(new Error("Failed to load Google Maps"));
    document.body.appendChild(s);
  });
  return loaderPromise;
}

const AddressAutocomplete = ({ onSelect }) => {
  const mountRef = useRef(null);
  const acRef = useRef(null);
  // Store onSelect in a ref so the mount effect below runs exactly ONCE
  // regardless of parent re-renders (parent's arrow-fn callback would
  // otherwise trigger a remount on every keystroke elsewhere in the form).
  const onSelectRef = useRef(onSelect);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!GMAPS_KEY) {
      setError("Maps key missing");
      setLoading(false);
      return;
    }
    let cancelled = false;
    let cleanupHandler = null;

    (async () => {
      try {
        await loadGoogleMaps();
        if (cancelled || !mountRef.current) return;

        const { PlaceAutocompleteElement } = await window.google.maps.importLibrary("places");
        if (cancelled || !mountRef.current) return;

        // Use `document.createElement` — the customElements upgrade path is
        // more reliable than `new` when the element hasn't been registered
        // yet in the browser's custom-elements registry.
        const ac = document.createElement("gmp-place-autocomplete");
        ac.setAttribute("placeholder", "Commencer à taper votre adresse…");
        ac.setAttribute("data-testid", "address-autocomplete-input");
        ac.className = "gv-pac-element";

        mountRef.current.innerHTML = "";
        mountRef.current.appendChild(ac);
        acRef.current = ac;

        // Wait for the custom element to finish upgrading — its shadow root
        // renders the <input> asynchronously after `connectedCallback`.
        // `whenDefined` returns immediately if already registered.
        try {
          await window.customElements.whenDefined("gmp-place-autocomplete");
        } catch (_) {
          /* not blocking */
        }

        const handleSelect = async (event) => {
          try {
            const prediction = event.placePrediction || event.detail?.placePrediction;
            const place = prediction
              ? prediction.toPlace()
              : (event.place || event.detail?.place);
            if (!place) return;

            await place.fetchFields({
              fields: ["addressComponents", "formattedAddress"],
            });

            const comps = place.addressComponents || [];
            const find = (type) => comps.find((c) => (c.types || []).includes(type));
            const longText = (type) => find(type)?.longText || "";
            const shortText = (type) => find(type)?.shortText || "";

            const streetNumber = longText("street_number");
            const route = longText("route");
            // Read onSelect from the ref so we always call the current version
            onSelectRef.current({
              addressLine1: [streetNumber, route].filter(Boolean).join(" ").trim(),
              city: longText("locality") || longText("postal_town"),
              postCode: longText("postal_code"),
              country: shortText("country").toUpperCase(),
              state: shortText("administrative_area_level_1"),
            });
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[address-autocomplete] fetchFields failed:", err);
          }
        };
        ac.addEventListener("gmp-select", handleSelect);
        cleanupHandler = () => ac.removeEventListener("gmp-select", handleSelect);

        if (!cancelled) setLoading(false);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[address-autocomplete] init failed:", err);
        if (!cancelled) {
          setError(err.message || "Maps init failed");
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (cleanupHandler) cleanupHandler();
      if (acRef.current && acRef.current.parentElement) {
        acRef.current.parentElement.removeChild(acRef.current);
      }
      acRef.current = null;
    };
    // Empty deps: mount once, never remount on parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="col-span-2 relative" data-testid="address-autocomplete">
      <MapPin
        size={14}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--gv-pink)] z-10 pointer-events-none"
      />
      {loading && (
        <Loader2
          size={14}
          className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[color:var(--gv-ink)]/40 z-10 pointer-events-none"
        />
      )}
      {/* Host node — the PlaceAutocompleteElement web component mounts here */}
      <div ref={mountRef} className="gv-pac-host" />
      {error ? (
        <input
          placeholder="Saisir l'adresse manuellement…"
          className="gv-input w-full pl-9"
          data-testid="address-autocomplete-fallback"
        />
      ) : (
        <div className="text-[11px] text-[color:var(--gv-ink)]/50 mt-1">
          ✨ Suggestions Google — sélectionnez pour remplir automatiquement
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
